import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  useGenerateContract,
  useQuoteDetail,
  useQuoteTransitions,
  QUOTE_STATUS_LABELS,
  type QuoteStatus,
} from '@/hooks/useQuoteContract';
import {
  QUOTE_CONTRACT_PERMISSIONS,
  canSeeQuoteContractMenu,
  hasPermission,
} from '@/lib/quoteContractPermissions';

const BADGE: Record<QuoteStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  submitted: 'bg-blue-100 text-blue-800',
  price_review_approved: 'bg-green-100 text-green-800',
  contract_generated: 'bg-emerald-100 text-emerald-900',
  changes_requested: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-zinc-200 text-zinc-700',
  superseded: 'bg-zinc-200 text-zinc-700',
};

export default function QuoteDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id!;
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const canRead = canSeeQuoteContractMenu(permissions);
  const canUpdate = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.UPDATE,
  );
  const canReview = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.REVIEW,
  );
  const canContract = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.CONTRACT,
  );

  const { quote, reviews, loading, error, fetchQuote, setQuote } =
    useQuoteDetail();
  const transitions = useQuoteTransitions();
  const generation = useGenerateContract();

  const [reviewerComment, setReviewerComment] = useState('');
  const [pricingPolicyId, setPricingPolicyId] = useState('');
  const [specialApprovalRef, setSpecialApprovalRef] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [contractSnapshot, setContractSnapshot] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (canRead) void fetchQuote(id);
  }, [canRead, fetchQuote, id]);

  if (!canRead) return <AccessDenied resource="报价 / 合同" />;

  const refresh = (next: typeof quote) => {
    if (next) setQuote(next);
    void fetchQuote(id);
  };

  const handleSubmit = async () => {
    setActionError(null);
    if (!quote) return;
    const next = await transitions.submit(id, { expectedVersion: quote.version });
    if (next) refresh(next);
    else if (transitions.error) setActionError(transitions.error.message);
  };

  const handleApprove = async () => {
    setActionError(null);
    if (!quote) return;
    const next = await transitions.approve(id, {
      expectedVersion: quote.version,
      reviewerComment: reviewerComment || undefined,
      pricingPolicyId: pricingPolicyId || undefined,
      specialApprovalRef: specialApprovalRef || undefined,
    });
    if (next) refresh(next);
    else if (transitions.error) setActionError(transitions.error.message);
  };

  const handleReject = async () => {
    setActionError(null);
    if (!quote) return;
    if (!reviewerComment.trim()) {
      setActionError('拒绝必须填写复核意见');
      return;
    }
    const next = await transitions.reject(id, {
      expectedVersion: quote.version,
      reviewerComment,
    });
    if (next) refresh(next);
    else if (transitions.error) setActionError(transitions.error.message);
  };

  const handleRequestChanges = async () => {
    setActionError(null);
    if (!quote) return;
    if (!reviewerComment.trim()) {
      setActionError('打回必须填写复核意见');
      return;
    }
    const next = await transitions.requestChanges(id, {
      expectedVersion: quote.version,
      reviewerComment,
    });
    if (next) refresh(next);
    else if (transitions.error) setActionError(transitions.error.message);
  };

  const handleCancel = async () => {
    setActionError(null);
    if (!quote) return;
    if (!cancelReason.trim()) {
      setActionError('取消必须填写原因');
      return;
    }
    const next = await transitions.cancel(id, {
      expectedVersion: quote.version,
      reason: cancelReason,
    });
    if (next) refresh(next);
    else if (transitions.error) setActionError(transitions.error.message);
  };

  const handleGenerateContract = async () => {
    setActionError(null);
    if (!quote) return;
    if (!contractSnapshot.trim()) {
      setActionError('合同正文不能为空');
      return;
    }
    const contract = await generation.generate(id, {
      expectedQuoteVersion: quote.version,
      contentSnapshot: contractSnapshot,
    });
    if (contract) {
      navigate(`/quote-contract/contracts/${contract.id}`);
    } else if (generation.error) {
      setActionError(generation.error.message);
    }
  };

  if (loading && !quote) {
    return <Card className="m-6 p-6">加载中…</Card>;
  }
  if (error) {
    return (
      <Card className="m-6 bg-destructive/10 p-6 text-sm text-destructive">
        加载失败：{error.message}
      </Card>
    );
  }
  if (!quote) return null;

  const editable =
    quote.status === 'draft' || quote.status === 'changes_requested';
  const canFinanceReview = quote.status === 'submitted' && canReview;
  const canGenerateContract =
    quote.status === 'price_review_approved' && canContract;

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-semibold">{quote.title}</h1>
          <p className="text-sm text-muted-foreground">
            报价编号 {quote.quoteNo} · 客户 {quote.customerId} · 方案{' '}
            {quote.proposalId} (版本 {quote.proposalVersionId})
          </p>
        </div>
        <span
          className={`ml-auto rounded-full px-3 py-0.5 text-xs ${BADGE[quote.status]}`}
        >
          {QUOTE_STATUS_LABELS[quote.status]}
        </span>
      </div>

      {actionError && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          {actionError}
        </Card>
      )}

      <Card className="space-y-2 p-4 text-sm">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <div>
            <div className="text-xs text-muted-foreground">币种</div>
            <div className="font-mono">{quote.currency}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">定价总额</div>
            <div className="font-mono">{quote.totalListAmount}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">折扣总额</div>
            <div className="font-mono">{quote.totalDiscountAmount}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">成交总额</div>
            <div className="font-mono font-semibold">{quote.totalAmount}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">赠送成本</div>
            <div className="font-mono">{quote.totalGiftCost}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">毛利总额</div>
            <div className="font-mono">{quote.totalMargin}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">负责顾问</div>
            <div className="text-xs">{quote.ownerUserId}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">版本</div>
            <div className="text-xs">v{quote.version}</div>
          </div>
        </div>
        {quote.notes && (
          <div className="mt-2 rounded-md bg-muted/50 p-2 text-xs">
            备注：{quote.notes}
          </div>
        )}
      </Card>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">#</th>
              <th className="px-3 py-2 text-left font-medium">分类</th>
              <th className="px-3 py-2 text-left font-medium">名称</th>
              <th className="px-3 py-2 text-right font-medium">数量</th>
              <th className="px-3 py-2 text-left font-medium">单位</th>
              <th className="px-3 py-2 text-right font-medium">定价</th>
              <th className="px-3 py-2 text-right font-medium">折扣</th>
              <th className="px-3 py-2 text-right font-medium">实际单价</th>
              <th className="px-3 py-2 text-right font-medium">赠送成本</th>
              <th className="px-3 py-2 text-right font-medium">行金额</th>
            </tr>
          </thead>
          <tbody>
            {quote.items.map((item) => (
              <tr key={item.id} className="border-b text-xs">
                <td className="px-3 py-2">{item.itemNo}</td>
                <td className="px-3 py-2">{item.category}</td>
                <td className="px-3 py-2 font-medium">{item.name}</td>
                <td className="px-3 py-2 text-right font-mono">{item.quantity}</td>
                <td className="px-3 py-2">{item.unit}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {item.listUnitPrice}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {item.discountAmount}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {item.finalUnitPrice}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {item.giftCost}
                </td>
                <td className="px-3 py-2 text-right font-mono font-semibold">
                  {item.amount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {canUpdate && editable && (
        <Card className="space-y-2 p-4">
          <h3 className="font-medium">顾问操作</h3>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={transitions.loading}
            >
              提交财务复核
            </Button>
            <div className="flex flex-1 items-center gap-2">
              <Input
                placeholder="取消原因"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                disabled={transitions.loading}
              >
                取消报价
              </Button>
            </div>
          </div>
        </Card>
      )}

      {canFinanceReview && (
        <Card className="space-y-3 p-4">
          <h3 className="font-medium">财务成交价复核</h3>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="policy">适用政策 ID</Label>
              <Input
                id="policy"
                value={pricingPolicyId}
                onChange={(e) => setPricingPolicyId(e.target.value)}
                placeholder="policy_..."
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="special">特批参考号</Label>
              <Input
                id="special"
                value={specialApprovalRef}
                onChange={(e) => setSpecialApprovalRef(e.target.value)}
                placeholder="approval_..."
              />
            </div>
            <div className="space-y-1 md:col-span-3">
              <Label htmlFor="comment">复核意见</Label>
              <textarea
                id="comment"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={3}
                value={reviewerComment}
                onChange={(e) => setReviewerComment(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={handleApprove}
              disabled={transitions.loading}
            >
              复核通过
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRequestChanges}
              disabled={transitions.loading}
            >
              打回修改
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleReject}
              disabled={transitions.loading}
            >
              拒绝
            </Button>
          </div>
        </Card>
      )}

      {canGenerateContract && (
        <Card className="space-y-3 p-4">
          <h3 className="font-medium">生成合同</h3>
          <p className="text-xs text-muted-foreground">
            只有财务复核通过的报价才能生成合同。合同正文将作为快照保留。
          </p>
          <textarea
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            rows={6}
            value={contractSnapshot}
            onChange={(e) => setContractSnapshot(e.target.value)}
            placeholder="合同正文..."
          />
          <Button onClick={handleGenerateContract} disabled={generation.loading}>
            {generation.loading ? '生成中…' : '生成合同'}
          </Button>
        </Card>
      )}

      <Card className="space-y-2 p-4">
        <h3 className="font-medium">复核历史</h3>
        {reviews.length === 0 ? (
          <p className="text-xs text-muted-foreground">暂无复核记录</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {reviews.map((r) => (
              <li key={r.id} className="rounded-md border p-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {QUOTE_STATUS_LABELS[r.fromStatus]} →{' '}
                    {QUOTE_STATUS_LABELS[r.toStatus]}
                  </span>
                  <span className="text-muted-foreground">
                    {new Date(r.reviewedAt).toLocaleString('zh-CN')}
                  </span>
                </div>
                <div className="text-muted-foreground">
                  决策：{r.decision} · 操作人 {r.reviewedBy}
                </div>
                {r.reviewerComment && <div>{r.reviewerComment}</div>}
                {r.pricingPolicyId && (
                  <div className="text-muted-foreground">
                    应用政策：{r.pricingPolicyId}
                  </div>
                )}
                {r.specialApprovalRef && (
                  <div className="text-muted-foreground">
                    特批参考：{r.specialApprovalRef}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
